import { Stack, Group, Button, Text, ActionIcon } from '@repletoryreach/sdk-ui';
import { TextInput, Select } from '@repletoryreach/sdk-ui/inputs';
import { Tooltip } from '@repletoryreach/sdk-ui/overlays';
import { useT } from '../../../i18n/I18nProvider';
import { BUTTON_TYPES, type ButtonType, type DraftButton } from './draft';

/** Meta's ceilings, so the Add button switches off before the review does. */
const MAX_BUTTONS = 10;
const MAX_URL = 2;
const MAX_PHONE = 1;

/**
 * Buttons, as a form.
 *
 * Until now buttons existed only in Meta's schema: authoring one meant typing
 * `{"type":"BUTTONS","buttons":[{"type":"URL",…}]}` into a JSON textarea, so in
 * practice nobody used them and no template in the demo account had one. They
 * are the single biggest difference between a message that gets a reply and one
 * that does not.
 *
 * Each type is offered with what it does rather than with Meta's constant:
 * "Reply with a set answer", "Open a link", "Call a number".
 */
export function ButtonsBuilder({
  buttons,
  onChange
}: {
  buttons: DraftButton[];
  onChange: (next: DraftButton[]) => void;
}) {
  const t = useT();

  const urls = buttons.filter((b) => b.type === 'URL').length;
  const phones = buttons.filter((b) => b.type === 'PHONE_NUMBER').length;
  const full = buttons.length >= MAX_BUTTONS;

  function update(i: number, patch: Partial<DraftButton>) {
    onChange(buttons.map((b, idx) => (idx === i ? { ...b, ...patch } : b)));
  }

  function add(type: ButtonType) {
    onChange([...buttons, { type, text: '' }]);
  }

  return (
    <Stack gap="sm">
      <Group justify="space-between" align="center">
        <Text role="inputLabel">{t('templates.wizard.buttons')}</Text>
        <Text role="caption" c="var(--rr-color-text-muted)">
          {t('templates.wizard.buttons.count', { count: buttons.length, max: MAX_BUTTONS })}
        </Text>
      </Group>

      {buttons.length === 0 && (
        <Text role="caption" c="var(--rr-color-text-muted)">{t('templates.wizard.buttons.hint')}</Text>
      )}

      {buttons.map((b, i) => (
        <div key={i} className="rr-tplwiz__button">
          <Select
            label={t('templates.wizard.buttons.type')}
            value={b.type}
            onChange={(e) => update(i, { type: e.currentTarget.value as ButtonType, url: undefined, phone_number: undefined })}
            data={BUTTON_TYPES.map((type) => ({ value: type, label: t(`templates.wizard.buttons.type.${type.toLowerCase()}`) }))}
          />
          <TextInput
            label={t('templates.wizard.buttons.label')}
            description={t('templates.wizard.buttons.label.hint')}
            value={b.text}
            maxLength={25}
            onChange={(e) => update(i, { text: e.currentTarget.value })}
          />
          {b.type === 'URL' && (
            <TextInput
              label={t('templates.wizard.buttons.url')}
              description={t('templates.wizard.buttons.url.hint')}
              value={b.url ?? ''}
              onChange={(e) => update(i, { url: e.currentTarget.value })}
            />
          )}
          {b.type === 'PHONE_NUMBER' && (
            <TextInput
              label={t('templates.wizard.buttons.phone')}
              description={t('templates.wizard.buttons.phone.hint')}
              value={b.phone_number ?? ''}
              onChange={(e) => update(i, { phone_number: e.currentTarget.value })}
            />
          )}
          <Tooltip label={t('templates.wizard.buttons.remove')} position="top">
            <ActionIcon
              variant="subtle"
              size="sm"
              aria-label={t('templates.wizard.buttons.remove')}
              onClick={() => onChange(buttons.filter((_, idx) => idx !== i))}
            >
              <TrashIcon />
            </ActionIcon>
          </Tooltip>
        </div>
      ))}

      <Group gap="xs" wrap="wrap">
        <Button size="xs" variant="default" disabled={full} onClick={() => add('QUICK_REPLY')}>
          {t('templates.wizard.buttons.add.quick_reply')}
        </Button>
        <Button size="xs" variant="default" disabled={full || urls >= MAX_URL} onClick={() => add('URL')}>
          {t('templates.wizard.buttons.add.url')}
        </Button>
        <Button size="xs" variant="default" disabled={full || phones >= MAX_PHONE} onClick={() => add('PHONE_NUMBER')}>
          {t('templates.wizard.buttons.add.phone_number')}
        </Button>
      </Group>
    </Stack>
  );
}

function TrashIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor"
      strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M2.8 4.2h10.4M6.4 4.2V2.8h3.2v1.4M4.2 4.2l.6 8.4h6.4l.6-8.4" />
      <path d="M6.8 6.8v3.8M9.2 6.8v3.8" />
    </svg>
  );
}
